/**
 * Analytics Export Utilities
 * Export analytics data to CSV or trigger print-to-PDF
 */
import i18next from 'i18next';

interface AnalyticsData {
  orders: any;
  employees: any;
  products: any;
  inventory: any;
  financial: any;
}

// Format currency for CSV
const fmtCurrency = (n: number) => `€${Number(n || 0).toFixed(2)}`;
const fmtDate = (d: string) => {
  if (!d) return '';
  const date = new Date(d);
  return date.toLocaleDateString(i18next.language === 'el' ? 'el-GR' : 'en-GB');
};

const t = (key: string) => i18next.t(key);

// ─────────────────────────────────────────────────────────────────────────────
// Παραμετροποιήσιμη αναφορά PDF (επιλογή ενοτήτων από το ExportReportModal).
// Τα δεδομένα των ενοτήτων έρχονται πλήρη από το /report-data — όχι τα top-10
// του dashboard. Σχεδιασμένο για εκτύπωση: cover, KPI κάρτες, πίνακες με
// μπάρες μεριδίων, κάθε μεγάλη ενότητα σε νέα σελίδα.
// ─────────────────────────────────────────────────────────────────────────────

export interface ReportOptions {
  accounting: boolean;
  products: boolean;
  customers: boolean;
  channels: boolean;
  waste: boolean;
  detail: 'summary' | 'full';
}

interface CustomReportParams {
  analytics: AnalyticsData;      // ήδη φορτωμένα δεδομένα dashboard (Σύνοψη)
  report: any;                   // απάντηση του /report-data (ή null)
  options: ReportOptions;
  storeName?: string;
  dateFrom: string;
  dateTo: string;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  '11.1': 'Απόδειξη', '11.2': 'Απόδ. Υπηρεσίας', '1.1': 'Τιμολόγιο',
  '2.1': 'Τιμ. Υπηρεσίας', '8.6': 'Δελτίο Παραγγελίας', '9.3': 'Δελτίο Αποστολής', '11.4': 'Ακυρωτικό', '5.1': 'Πιστωτικό',
};
const CHANNEL_LABELS_PDF: Record<string, string> = { takeaway: 'Take away', delivery: 'Delivery', dine_in: 'Dine-in' };
const PLATFORM_LABELS_PDF: Record<string, string> = { efood: 'efood', wolt: 'Wolt', box: 'Box', own: 'Κατάστημα' };

export function exportCustomReportPDF({ analytics, report, options, storeName, dateFrom, dateTo }: CustomReportParams) {
  const o = analytics.orders || {};
  const lang = i18next.language || 'el';
  const esc = (s: any) => String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' } as any)[c]);
  const eur = (n: any) => fmtCurrency(Number(n) || 0);
  const pctOf = (part: number, total: number) => (total > 0 ? (part / total) * 100 : 0);

  // Μπάρα μεριδίου + ποσοστό — το βασικό οπτικό στοιχείο των πινάκων.
  const share = (part: number, total: number, color = '#2563eb') => {
    const p = pctOf(part, total);
    return `<div class="share"><div class="track"><i style="width:${Math.min(100, p).toFixed(1)}%;background:${color}"></i></div><span>${p.toFixed(1)}%</span></div>`;
  };

  const delta = (v: number) => {
    if (v == null || isNaN(v) || v === 0) return `<span class="chip chip-flat">—</span>`;
    const up = v > 0;
    return `<span class="chip ${up ? 'chip-up' : 'chip-down'}">${up ? '▲' : '▼'} ${Math.abs(v).toFixed(1)}%</span>`;
  };

  const sec = (title: string, color: string, body: string, breakBefore = true) =>
    `<div class="section${breakBefore ? ' brk' : ''}"><h2><span class="dot" style="background:${color}"></span>${title}</h2>${body}</div>`;

  const table = (heads: string[], rows: string, numCols: number[] = []) => {
    const ths = heads.map((h, i) => `<th${numCols.includes(i) ? ' class="num"' : ''}>${h}</th>`).join('');
    return `<table><thead><tr>${ths}</tr></thead><tbody>${rows}</tbody></table>`;
  };

  // ── Ενότητες που περιλαμβάνονται (chips στο cover) ──
  const included: string[] = ['Σύνοψη'];
  if (options.accounting && report?.accounting) included.push('Λογιστικά');
  if (options.products && report?.products) included.push(`Product Mix (${options.detail === 'full' ? 'αναλυτικό' : 'συνοπτικό'})`);
  if (options.customers && report?.customers) included.push('Πελάτες');
  if (options.channels && report?.channels) included.push('Κανάλια');
  if (options.waste && report?.waste) included.push('Φύρα');

  // ── 1. Σύνοψη ──
  const cmp = o.comparison || {};
  const peak = o.peak_hours && o.peak_hours[0] ? `${String(o.peak_hours[0].hour).split(':')[0]}:00` : null;
  const dailyRows = (o.daily_trend || []).map((d: any) =>
    `<tr><td>${fmtDate(d.date)}</td><td class="num">${d.orders}</td><td class="num strong">${eur(d.revenue)}</td></tr>`).join('');
  const summaryBody = `
    <div class="kpis">
      <div class="kpi a-blue"><div class="label">Παραγγελίες</div><div class="value">${o.total_orders ?? 0}</div><div class="foot">${delta(cmp.ordersChange)}</div></div>
      <div class="kpi a-green"><div class="label">Τζίρος</div><div class="value">${eur(o.total_revenue)}</div><div class="foot">${delta(cmp.revenueChange)}</div></div>
      <div class="kpi a-purple"><div class="label">Μέση παραγγελία</div><div class="value">${eur(o.average_order_value)}</div></div>
      <div class="kpi a-amber"><div class="label">Ώρα αιχμής</div><div class="value">${peak || '—'}</div></div>
    </div>
    ${dailyRows ? table(['Ημερομηνία', 'Παραγγελίες', 'Τζίρος'], dailyRows, [1, 2]) : ''}`;

  // ── 2. Λογιστικά ──
  let accountingBody = '';
  const acc = report?.accounting;
  if (options.accounting && acc) {
    const payRows = ([
      ['Μετρητά', acc.cash, '#16a34a'],
      ['Κάρτα', acc.card, '#ca8a04'],
      ['IRIS', acc.iris, '#0891b2'],
      ['Προπληρωμένες / Πλατφόρμες', acc.prepaid, '#7c3aed'],
    ] as [string, number, string][])
      .filter(([, v]) => Math.abs(v) > 0.001)
      .map(([l, v, c]) => `<tr><td>${l}</td><td class="num strong">${eur(v)}</td><td class="w-share">${share(v, acc.gross, c)}</td></tr>`)
      .join('');
    const docRows = (acc.documentTypes || []).map((t2: any) =>
      `<tr><td>${esc(DOC_TYPE_LABELS[t2.code] || t2.code)}</td><td class="num">${t2.count}</td><td class="num">${eur(t2.net)}</td><td class="num">${eur(t2.vat)}</td><td class="num strong">${eur(t2.gross)}</td></tr>`).join('');
    const vatRows = (acc.byVat || []).map((v: any) =>
      `<tr><td>ΦΠΑ ${v.rate != null ? `${v.rate}%` : '—'}</td><td class="num">${eur(v.net)}</td><td class="num">${eur(v.vat)}</td><td class="num strong">${eur(v.gross)}</td></tr>`).join('');
    // Τέλη/εισφορές αναλυτικά (σακούλες vs εισφορά πλαστικών) — όχι συγκεντρωτικά.
    const feeRows = (acc.fees || []).map((f: any) =>
      `<tr><td>${esc(f.label)}</td><td class="num">${f.units}</td><td class="num strong">${eur(f.amount)}</td></tr>`).join('');
    const feeTotal = (acc.fees || []).length > 1
      ? `<tr class="totrow"><td>Σύνολο τελών</td><td></td><td class="num strong">${eur(acc.plasticFees)}</td></tr>` : '';
    // Ανά πλατφόρμα — ίδια money buckets, πλήρης διαμέριση του συνόλου.
    const accPlatRows = (acc.byPlatform || []).map((pl: any) =>
      `<tr><td><b>${esc(PLATFORM_LABELS_PDF[pl.platform] || pl.platform)}</b></td><td class="num">${pl.count}</td><td class="num">${eur(pl.cash)}</td><td class="num">${eur(pl.card)}</td><td class="num">${eur(pl.iris)}</td><td class="num">${eur(pl.prepaid)}</td><td class="num strong">${eur(pl.gross)}</td></tr>`).join('');
    const accPlatTotal = (acc.byPlatform || []).length > 1
      ? `<tr class="totrow"><td>Σύνολο</td><td class="num">${acc.orders}</td><td class="num">${eur(acc.cash)}</td><td class="num">${eur(acc.card)}</td><td class="num">${eur(acc.iris)}</td><td class="num">${eur(acc.prepaid)}</td><td class="num strong">${eur(acc.gross)}</td></tr>` : '';
    accountingBody = `
      <h3>Εισπράξεις ανά τρόπο πληρωμής</h3>
      ${table(['Τρόπος', 'Ποσό', 'Μερίδιο'], payRows + `<tr class="totrow"><td>Σύνολο εισπράξεων</td><td class="num strong">${eur(acc.gross)}</td><td></td></tr>`, [1])}
      ${accPlatRows ? `<h3>Ανά πλατφόρμα</h3>${table(['Πηγή', 'Παραγγ.', 'Μετρητά', 'Κάρτα', 'IRIS', 'Προπληρ.', 'Σύνολο'], accPlatRows + accPlatTotal, [1, 2, 3, 4, 5, 6])}` : ''}
      ${docRows ? `<h3>Ανά τύπο παραστατικού</h3>${table(['Παραστατικό', 'Πλήθος', 'Καθαρό', 'ΦΠΑ', 'Σύνολο'], docRows, [1, 2, 3, 4])}` : ''}
      ${vatRows ? `<h3>Ανάλυση ΦΠΑ</h3>${table(['Συντελεστής', 'Καθαρό', 'ΦΠΑ', 'Σύνολο'], vatRows, [1, 2, 3])}` : ''}
      ${feeRows ? `<h3>Τέλη & Εισφορές</h3>${table(['Τέλος', 'Τεμ.', 'Ποσό'], feeRows + feeTotal, [1, 2])}` : ''}
      <p class="finenote">Οι εισπράξεις ακολουθούν τους κανόνες του κλεισίματος ταμείου — παραγγελίες με διανομή πλατφόρμας εμφανίζονται στις Προπληρωμένες (εξοφλούνται στη μηνιαία εκκαθάριση).</p>`;
  }

  // ── 3. Product Mix ──
  let productsBody = '';
  const pm = report?.products;
  if (options.products && pm) {
    const catTotal = (pm.categories || []).reduce((s: number, c: any) => s + c.revenue, 0);
    const catRows = (pm.categories || []).map((c: any) =>
      `<tr><td>${esc(c.category)}</td><td class="num">${c.qty}</td><td class="num strong">${eur(c.revenue)}</td><td class="w-share">${share(c.revenue, catTotal, '#7c3aed')}</td></tr>`).join('');
    const prodTotal = (pm.all || []).reduce((s: number, p: any) => s + p.revenue, 0);
    const prodRows = (pm.all || []).map((p: any, i: number) =>
      `<tr><td><span class="rank">${i + 1}</span> ${esc(p.name)}</td><td class="muted">${esc(p.category)}</td><td class="num">${p.qty}</td><td class="num strong">${eur(p.revenue)}</td><td class="w-share">${share(p.revenue, prodTotal, '#2563eb')}</td></tr>`).join('');
    // Προσφορές ανά deal × πλατφόρμα («Φορές» = πόσες φορές δόθηκε το πακέτο).
    const offerRows = (pm.offers || []).map((o: any) =>
      `<tr><td>${esc(o.offer)}</td><td>${esc(PLATFORM_LABELS_PDF[o.platform] || o.platform)}</td><td class="num">${o.times}</td><td class="num strong">${eur(o.revenue)}</td></tr>`).join('');
    productsBody = `
      ${catRows ? `<h3>Κατηγορίες</h3>${table(['Κατηγορία', 'Τεμ.', 'Τζίρος', 'Μερίδιο'], catRows, [1, 2])}` : ''}
      ${offerRows ? `<h3>Προσφορές (πακέτα) ανά πλατφόρμα</h3>${table(['Προσφορά', 'Πλατφόρμα', 'Φορές', 'Τζίρος'], offerRows, [2, 3])}` : ''}
      <h3>Όλα τα προϊόντα (${(pm.all || []).length})</h3>
      ${table(['Προϊόν', 'Κατηγορία', 'Τεμ.', 'Τζίρος', 'Μερίδιο'], prodRows, [2, 3])}`;

    if (options.detail === 'full') {
      const sizeRows = (pm.sizes || []).map((s2: any) =>
        `<tr><td>${esc(s2.product)}</td><td>${esc(s2.size)}</td><td class="num strong">${s2.qty}</td></tr>`).join('');
      const optRows = (pm.options || []).map((op2: any) =>
        `<tr><td>${esc(op2.group_name)}</td><td>${esc(op2.choice)}</td><td class="num strong">${op2.qty}</td></tr>`).join('');
      const exRows = (pm.extras || []).map((ex: any) =>
        `<tr><td>${esc(ex.name)}</td><td class="num">${ex.qty}</td><td class="num strong">${eur(ex.revenue)}</td></tr>`).join('');
      productsBody += `
        ${sizeRows ? `<h3>Μεγέθη</h3>${table(['Προϊόν', 'Μέγεθος', 'Τεμ.'], sizeRows, [2])}` : ''}
        ${optRows ? `<h3>Επιλογές (options)</h3>${table(['Ομάδα', 'Επιλογή', 'Φορές'], optRows, [2])}` : ''}
        ${exRows ? `<h3>Extras / Υλικά</h3>${table(['Extra', 'Τεμ.', 'Τζίρος'], exRows, [1, 2])}` : ''}`;
    }
  }

  // ── 4. Πελάτες ──
  let customersBody = '';
  const cu = report?.customers;
  if (options.customers && cu) {
    const s2 = cu.summary || {};
    const ret = cu.retention || {};
    const topRows = (cu.top_by_revenue || []).map((c: any, i: number) =>
      `<tr><td><span class="rank">${i + 1}</span> ${esc(c.name || '—')}</td><td class="muted">${esc(c.phone || '')}</td><td class="num">${c.total_orders}</td><td class="num strong">${eur(c.total_spent)}</td><td class="num">${eur(c.average_order_value)}</td></tr>`).join('');
    const freqRows = (cu.order_frequency || []).map((f: any) =>
      `<tr><td>${esc(f.bucket)}</td><td class="num strong">${f.count}</td></tr>`).join('');
    customersBody = `
      <div class="kpis">
        <div class="kpi a-blue"><div class="label">Σύνολο πελατών</div><div class="value">${s2.total_customers ?? 0}</div></div>
        <div class="kpi a-green"><div class="label">Νέοι στην περίοδο</div><div class="value">${s2.new_in_period ?? 0}</div></div>
        <div class="kpi a-purple"><div class="label">Επαναλαμβανόμενοι</div><div class="value">${ret.returning ?? 0}</div></div>
        <div class="kpi a-amber"><div class="label">Μ.ο. αξίας πελάτη</div><div class="value">${eur(s2.avg_spent_per_customer)}</div></div>
      </div>
      ${topRows ? `<h3>Κορυφαίοι πελάτες (τζίρος)</h3>${table(['Πελάτης', 'Τηλέφωνο', 'Παραγγ.', 'Σύνολο', 'Μ.Ο.'], topRows, [2, 3, 4])}` : ''}
      ${freqRows ? `<h3>Συχνότητα παραγγελιών</h3>${table(['Κατηγορία', 'Πελάτες'], freqRows, [1])}` : ''}`;
  }

  // ── 5. Κανάλια ──
  let channelsBody = '';
  const ch = report?.channels;
  if (options.channels && ch) {
    const chTotal = (ch.byChannel || []).reduce((s3: number, c: any) => s3 + c.gross, 0);
    const chRows = (ch.byChannel || []).map((c: any) => {
      const pays = ([['Μετρητά', c.cash], ['Κάρτα', c.card], ['IRIS', c.iris], ['Προπληρ.', c.prepaid]] as [string, number][])
        .filter(([, v]) => Math.abs(v) > 0.001)
        .map(([l, v]) => `${l} ${eur(v)}`).join(' · ');
      return `<tr><td><b>${esc(CHANNEL_LABELS_PDF[c.channel] || c.channel)}</b><div class="muted small">${pays}${c.peakHour != null ? ` · αιχμή ${c.peakHour}:00` : ''}</div></td><td class="num">${c.count}</td><td class="num">${eur(c.aov)}</td><td class="num strong">${eur(c.gross)}</td><td class="w-share">${share(c.gross, chTotal, '#d97706')}</td></tr>`;
    }).join('');
    const plTotal = (ch.byPlatform || []).reduce((s3: number, p: any) => s3 + p.gross, 0);
    const plRows = (ch.byPlatform || []).map((p: any) =>
      `<tr><td>${esc(PLATFORM_LABELS_PDF[p.platform] || p.platform)}</td><td class="num">${p.count}</td><td class="num">${eur(p.aov)}</td><td class="num strong">${eur(p.gross)}</td><td class="w-share">${share(p.gross, plTotal, '#0891b2')}</td></tr>`).join('');
    channelsBody = `
      ${chRows ? `<h3>Κανάλια εξυπηρέτησης</h3>${table(['Κανάλι', 'Παραγγ.', 'Μ.Ο.', 'Τζίρος', 'Μερίδιο'], chRows, [1, 2, 3])}` : ''}
      ${plRows ? `<h3>Πηγές παραγγελιών</h3>${table(['Πηγή', 'Παραγγ.', 'Μ.Ο.', 'Τζίρος', 'Μερίδιο'], plRows, [1, 2, 3])}` : ''}`;
  }

  // ── 6. Φύρα ── (αξία σε τιμές πώλησης — δεν συμμετέχει στα χρηματικά σύνολα)
  let wasteBody = '';
  const wa = report?.waste;
  if (options.waste && wa && wa.summary) {
    const wReasonLabels: Record<string, string> = { expired: 'Ληγμένο', damaged: 'Κατεστραμμένο', misprep: 'Λάθος παρασκευή', other: 'Άλλο' };
    const wProdRows = (wa.byProduct || []).map((p: any) =>
      `<tr><td>${esc(p.product_name)}</td><td class="num">${p.qty}</td><td class="num strong">${eur(p.value)}</td><td class="w-share">${share(p.value, wa.summary.value, '#ea580c')}</td></tr>`).join('');
    const wReasonRows = (wa.byReason || []).map((r: any) =>
      `<tr><td>${esc(wReasonLabels[r.reason] || r.reason)}</td><td class="num">${r.qty}</td><td class="num strong">${eur(r.value)}</td></tr>`).join('');
    const wDayRows = (wa.byDay || []).map((d: any) =>
      `<tr><td>${fmtDate(d.day)}</td><td class="num">${d.qty}</td><td class="num strong">${eur(d.value)}</td></tr>`).join('');
    const wStoreRows = (wa.byStore || []).map((s3: any) =>
      `<tr><td>${esc(s3.store)}</td><td class="num">${s3.qty}</td><td class="num strong">${eur(s3.value)}</td><td class="w-share">${share(s3.value, wa.summary.value, '#ea580c')}</td></tr>`).join('');
    wasteBody = `
      <div class="kpis">
        <div class="kpi a-amber"><div class="label">Καταχωρήσεις</div><div class="value">${wa.summary.entries}</div></div>
        <div class="kpi a-amber"><div class="label">Τεμάχια</div><div class="value">${wa.summary.qty}</div></div>
        <div class="kpi a-amber"><div class="label">Αξία (τιμές πώλησης)</div><div class="value">${eur(wa.summary.value)}</div></div>
      </div>
      ${wStoreRows ? `<h3>Ανά κατάστημα</h3>${table(['Κατάστημα', 'Τεμ.', 'Αξία', 'Μερίδιο'], wStoreRows, [1, 2])}` : ''}
      ${wProdRows ? `<h3>Ανά προϊόν</h3>${table(['Προϊόν', 'Τεμ.', 'Αξία', 'Μερίδιο'], wProdRows, [1, 2])}` : ''}
      ${wReasonRows ? `<h3>Ανά λόγο</h3>${table(['Λόγος', 'Τεμ.', 'Αξία'], wReasonRows, [1, 2])}` : ''}
      ${wDayRows ? `<h3>Ανά ημέρα</h3>${table(['Ημερομηνία', 'Τεμ.', 'Αξία'], wDayRows, [1, 2])}` : ''}
      <div class="finenote">Η αξία της φύρας υπολογίζεται σε τρέχουσες τιμές πώλησης και δεν συμμετέχει στα χρηματικά σύνολα της αναφοράς.</div>`;
  }

  const html = `<!DOCTYPE html>
<html lang="${lang}">
<head>
<meta charset="utf-8">
<title>Αναφορά — ${esc(storeName || '')}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif; color: #0f172a; font-size: 11.5px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { padding: 28px 36px; }
  .cover { background: linear-gradient(125deg, #1e1b4b 0%, #1e3a8a 55%, #2563eb 100%); color: #fff; border-radius: 16px; padding: 26px 30px; margin-bottom: 20px; }
  .cover h1 { font-size: 24px; font-weight: 800; letter-spacing: -0.4px; }
  .cover .sub { font-size: 12.5px; opacity: 0.9; margin-top: 6px; font-weight: 600; }
  .cover .gen { font-size: 10px; opacity: 0.65; margin-top: 3px; }
  .cover .tags { margin-top: 12px; display: flex; flex-wrap: wrap; gap: 6px; }
  .cover .tag { background: rgba(255,255,255,0.14); border: 1px solid rgba(255,255,255,0.25); border-radius: 999px; padding: 3px 11px; font-size: 10px; font-weight: 700; }
  .section { margin-top: 6px; }
  .section.brk { page-break-before: always; padding-top: 8px; }
  h2 { font-size: 15px; font-weight: 800; margin: 18px 0 10px; color: #0f172a; display: flex; align-items: center; gap: 9px; letter-spacing: -0.2px; }
  h2 .dot { width: 10px; height: 10px; border-radius: 3px; display: inline-block; }
  h3 { font-size: 11.5px; font-weight: 700; margin: 14px 0 6px; color: #334155; text-transform: uppercase; letter-spacing: 0.5px; }
  .kpis { display: flex; gap: 10px; margin: 8px 0 12px; }
  .kpi { flex: 1; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px 14px; border-top: 3px solid #94a3b8; }
  .kpi.a-blue { border-top-color: #2563eb; } .kpi.a-green { border-top-color: #16a34a; }
  .kpi.a-purple { border-top-color: #7c3aed; } .kpi.a-amber { border-top-color: #d97706; }
  .kpi .label { font-size: 9.5px; color: #64748b; text-transform: uppercase; letter-spacing: 0.4px; font-weight: 700; }
  .kpi .value { font-size: 19px; font-weight: 800; color: #0f172a; margin-top: 3px; }
  .kpi .foot { margin-top: 5px; }
  .chip { font-size: 9.5px; font-weight: 700; padding: 2px 7px; border-radius: 999px; display: inline-block; }
  .chip-up { background: #dcfce7; color: #15803d; } .chip-down { background: #fee2e2; color: #b91c1c; } .chip-flat { background: #f1f5f9; color: #64748b; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
  th { background: #f1f5f9; text-align: left; padding: 6px 9px; font-size: 9.5px; color: #475569; text-transform: uppercase; letter-spacing: 0.4px; border-bottom: 2px solid #cbd5e1; }
  th.num, td.num { text-align: right; }
  td { padding: 5px 9px; border-bottom: 1px solid #eef2f7; vertical-align: middle; }
  td.strong { font-weight: 700; }
  td.muted, .muted { color: #94a3b8; }
  .small { font-size: 9.5px; margin-top: 2px; }
  tr:nth-child(even) td { background: #fafbfc; }
  tr.totrow td { background: #eef2ff; font-weight: 800; border-top: 2px solid #c7d2fe; }
  .rank { display: inline-block; min-width: 17px; text-align: center; background: #2563eb; color: #fff; border-radius: 4px; font-size: 9px; font-weight: 700; padding: 1.5px 4px; margin-right: 5px; }
  .w-share { width: 110px; }
  .share { display: flex; align-items: center; gap: 6px; }
  .share .track { flex: 1; height: 6px; background: #e2e8f0; border-radius: 999px; overflow: hidden; }
  .share .track i { display: block; height: 100%; border-radius: 999px; }
  .share span { font-size: 9.5px; color: #64748b; font-weight: 700; min-width: 34px; text-align: right; }
  .note { margin: 8px 0; font-size: 11px; }
  .finenote { margin-top: 10px; font-size: 9.5px; color: #94a3b8; font-style: italic; }
  .footer { margin-top: 24px; padding-top: 10px; border-top: 1px solid #e2e8f0; font-size: 9.5px; color: #94a3b8; text-align: center; }
  @media print { .page { padding: 12px 16px; } h2, h3 { page-break-after: avoid; } table { page-break-inside: auto; } tr { page-break-inside: avoid; } .kpis { page-break-inside: avoid; } }
</style>
</head>
<body>
<div class="page">
  <div class="cover">
    <h1>${esc(storeName || 'Κατάστημα')}</h1>
    <div class="sub">Αναφορά Αναλυτικών · ${fmtDate(dateFrom)} – ${fmtDate(dateTo)}</div>
    <div class="gen">Δημιουργήθηκε: ${new Date().toLocaleString(lang === 'el' ? 'el-GR' : 'en-GB')}</div>
    <div class="tags">${included.map(x => `<span class="tag">${esc(x)}</span>`).join('')}</div>
  </div>

  ${sec('Σύνοψη', '#2563eb', summaryBody, false)}
  ${accountingBody ? sec('Λογιστικά', '#16a34a', accountingBody) : ''}
  ${productsBody ? sec('Product Mix', '#7c3aed', productsBody) : ''}
  ${customersBody ? sec('Ανάλυση Πελατών', '#0891b2', customersBody) : ''}
  ${channelsBody ? sec('Κανάλια Εξυπηρέτησης', '#d97706', channelsBody) : ''}
  ${wasteBody ? sec('Φύρα', '#ea580c', wasteBody) : ''}

  <div class="footer">${esc(storeName || '')} · Xentrix Flow · ${fmtDate(dateFrom)} – ${fmtDate(dateTo)}</div>
</div>
</body></html>`;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  }
}
